import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { NavigationService } from '../../../core/navigation.service';
import { MainRoutes } from '../../../core/enums';
import { DecimalPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { LetDirective } from '@ngrx/component';

interface Breadcrumb {
  title: string,
  url: string
}

@Component({
    selector: 'app-breadcrumbs',
    templateUrl: './breadcrumbs.component.html',
    styleUrls: ['./breadcrumbs.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
  imports: [
    MatIconButton,
    MatIcon,
    DecimalPipe,
    LetDirective,
  ],
})
export class BreadcrumbsComponent implements OnDestroy {

  // currently used only for address list + modes
  // the amount of selected items (if there are any selected) will be shown instead of breadcrumbs
  @Input() selectedItems: number;
  // since this is meant to be reusable, provide selection item name
  // is only required if showing the amount of selected items is required
  @Input() itemName: string;
  @Input() itemsTotal: string;

  // Subject emitting the breadcrumb hierarchy
  private breadcrumbs: BehaviorSubject<Breadcrumb[]> = new BehaviorSubject([]);
  private componentDestroyed: Subject<void> = new Subject<void>();

  public breadcrumbs$ = this.breadcrumbs.asObservable();
  public mainRoutes = MainRoutes;

  constructor(
      private router: Router,
      public translate: TranslateService,
      private navigationService: NavigationService,
      private cd: ChangeDetectorRef) {
    this.initBreadcrumbs();

    this.router.events.pipe(
        // Filter the NavigationEnd events as the breadcrumb is updated only when the route reaches its end
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.componentDestroyed),
    ).subscribe(() => {
      // Construct the breadcrumb hierarchy
      this.initBreadcrumbs();
    });
    // make sure the title changes on language change
    this.translate.onLangChange
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe(() => this.cd.markForCheck());
  }

  ngOnDestroy(): void {
    this.componentDestroyed.next();
  }

  private initBreadcrumbs() {
    let route = this.router.routerState.snapshot.root;
    let urlFragments: string[] = [];
    const breadcrumbs: Breadcrumb[] = [];

    while (route) {
      urlFragments = urlFragments.concat(route.url.map(url => url.path));

      if (route.title) {
        breadcrumbs.push({
          title: route.title,
          url: '/' + urlFragments.join('/'),
        });
      }

      route = route.firstChild;
    }

    this.breadcrumbs.next(breadcrumbs);
  }

  /**
   * Get title translation
   * @param title
   */
  public getLabel(title: string): string {
    return this.translate
        .instant(`PAGE.${title.replace(/-/g, '_').toUpperCase()}`);
  }

  /**
   * Navigate to another route by clicking on a breadcrumb. Will apply the most recent query params
   * @param bc
   */
  public navigate(bc: Breadcrumb): void {
    // make sure to get array filter values as strings, since the internal Angular
    // route builder will set the in a different format than we'd like
    // https://stackoverflow.com/questions/57063455/angular-router-navigate-use-array-as-list-of-queryparams
    // TODO: q params can be gotten from ngrx router store
    const params = this.navigationService.parseUrl(null, { arrayFilterKeys: [] });

    this.navigationService.navigate([bc.url], {
      queryParams: params,
    }).then();
  }

}
